import { Modal } from "@mui/material";
import React, { useRef, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import CustomButton from "../../../components/CustomButton";
import useCustomHook from "../../../reducers/ContentCreateHook";
import {LoadingSpinner} from '../../../components/Icons';
import CustomIconButton from "../../../components/CustomIconButton";
import { formatPadStartNumber, formatName, formatDateTime } from "../../../utils/format";
import CustomDatePicker from "../../../components/CustomDatePicker";
import TimeInput from "../../../components/TimeInput";
import CustomSwitch from "../../../components/CustomSwitch";
import CustomTextarea from "../../../components/CustomTextarea";
import TextInput from "../../../components/TextInput";
import JobPostPanel from "../../company_chat/components/JobPostPanel";
import {
  setIsOpenListJobPanel, setJobSelected, setListJobRecommend, 
} from "reducers/chatMessage";
import { saveBuldMessage, updateBuldMessage, bulkMessagesActions, getIsDisabledBtnSend } from "reducers/bulkMessages";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { useSnackbar } from "notistack";
import CustomToast from "../../../components/CustomToast";
import { MAX_FILE_SIZE, MESSAGE_TYPE, STAFF_TYPE } from "../../../constants/index";
import { validateFieldReq } from "../../../utils/validation";
import { downloadFile, getMinPublicTime, getTimeErrorMessage } from "../../../utils/util";
import i18n from 'i18next';
import dayjs from 'dayjs';
import { range } from "lodash";
import { getYear } from "date-fns";
import CustomTooltip from "../../../components/CustomTooltip";

const initFormData = {
  talents: [],
  jobID: null,
  isPublicScheduled: false,
  publicScheduleTime: null,
  content: '',
  files: [],
}

const initErrorData = {
  talents: null,
  jobID: null,
  publicScheduleTime: null,
  publicScheduleHours: null,
  publicScheduleMinutes: null,
  content: '',
  files: null,
}

const BulkScoutingDialog = ({ open, onClose, onConfirm, onConfirmPopupOpen }: any) => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const navigate = useNavigate();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const {staffInfo} = useSelector((state: any) => state.currentUser);
  const { jobSelected, isOpenListJobPanel } = useSelector((state: any) => state.chatMessage);
  const { recipients, chatType, isLoadingSaveSchedule, actionStatus, actionName,
    resMessage, isSuccess, bulkMessageDetail, bulkingMessageList, isResetForm, viewDetail, isSaving
  } = useSelector((state: any) => state.bulkMessages);
  const inputRef = useRef(null);
  const [files, setFiles] = useState<any[]>([]);
  const [recipientName, setRecipientName] = useState([]);
  const [formData, setFormData] = useState(initFormData);
  const [errors, setErrors] = useState(initErrorData);
  const rangeYears = range(getYear(new Date()), getYear(new Date()) + 5, 1);
  const isDisabledBtnSend = useSelector(getIsDisabledBtnSend(formData, files));
  const [oldPublicDate, setOldPublicDate] = useState('');
  const [isExpand, setIsExpand] = useState(false);
  const textRef = useRef<HTMLParagraphElement | null>(null);
  const [isTextOverflow, setIsTextOverflow] = useState(false);

  useEffect(() => {
    if(!open) {
      setIsExpand(false);
    }
    setErrors(initErrorData);
  }, [open])

  let publicTimeErrorMsg = i18n.t('MSG_GLOBAL_BULK_MESSAGE_SCHEDULE_MIN');
  if(chatType == MESSAGE_TYPE.SCOUT) {
    publicTimeErrorMsg = i18n.t('MSG_GLOBAL_BULK_SCOUT_MESSAGE_SCHEDULE_MIN');
  }

  let formErrors: any = {};

  useEffect(() => {
    setFormData(initFormData)
    setFiles([])
    setErrors({})
  }, [isResetForm]);

  useEffect(() => {
    dispatch(bulkMessagesActions.setNeedNewFormData(false));
  }, [formData]);

  useEffect(() => {
    if (recipients.length > 0) {
      const recipientNameData = recipients.map(item => formatName(item.first_name, item.last_name));
      setRecipientName(recipientNameData);
      setFormData((prev: any) => ({
        ...prev,
        talents: recipients.map(item => item.talent_id),
      }));
    }
  }, [recipients]);

  useEffect(() => {
    if(bulkMessageDetail?.public_time) {
      setOldPublicDate(dayjs(bulkMessageDetail?.public_time).format('YYYY-MM-DD HH:mm'));
    }
  }, [bulkMessageDetail]);

  useEffect(() => {
      if (actionStatus === 'fulfilled') {
        switch(actionName) {
          case 'saveBuldMessage':
            showCustomToast({message: resMessage, variant: isSuccess ? "success" : "error"});
            dispatch(bulkMessagesActions.setActionStatus('idle'));
            if(isSuccess) {
              onClose()
            }
            break;
          case 'updateBuldMessage':
            showCustomToast({message: resMessage, variant: isSuccess ? "success" : "error"});
            dispatch(bulkMessagesActions.setActionStatus('idle'));
            if(isSuccess) {
              const newBulkMessageList = bulkingMessageList.map((item) =>
                item.id === bulkMessageDetail?.id
                  ? { ...item, ...bulkMessageDetail }
                  : item
              );
              
              dispatch(bulkMessagesActions.setBulkingMessageList(newBulkMessageList));

              onClose()
            }
            break;
          case 'getBuldMessageDetail':
            dispatch(bulkMessagesActions.setActionStatus('idle'));
            if(!bulkMessageDetail?.id) {
              break
            }
            dispatch(bulkMessagesActions.setChatType(bulkMessageDetail?.chat_type?.value));

            // Job
            let job = bulkMessageDetail?.job
            dispatch(setJobSelected({...job}));

            // Talent
            const talents = []
            for (const item of bulkMessageDetail?.sct_schedule_chat) {
              const talent = {
                talent_id: item?.talent?.id,
                first_name: item?.talent?.first_name,
                last_name: item?.talent?.last_name
              }
              talents.push(talent)
            }
            dispatch(bulkMessagesActions.setRecipients(talents));

            // Public time
            let scheduleDate = null
            let scheduleHours = null
            let scheduleMinutes = null
            if (bulkMessageDetail?.public_time) {
              let public_time = new Date(bulkMessageDetail?.public_time)
              scheduleDate = public_time
              scheduleHours = public_time.getHours().toString().padStart(2, "0");
              scheduleMinutes = public_time.getMinutes().toString().padStart(2, "0");
            }

            setFormData(() => ({
              ...formData,
              ['jobID']: job?.id,
              ['isPublicScheduled']: bulkMessageDetail?.is_public_scheduled,
              ['publicScheduleTime']: scheduleDate,
              ['publicScheduleHours']: scheduleHours,
              ['publicScheduleMinutes']: scheduleMinutes,
              ['content']: bulkMessageDetail?.chat_content,
            }));

            // Files
            const files = []
            for(const item of bulkMessageDetail?.scf_schedule_chat) {
              files.push(item)
            }
            setFiles(files)

            break;
          default:
            break;
        }
      }
  }, [actionStatus, actionName]);

  const showCustomToast = ({title="メッセージ", message, variant="success", type="normal"}) => {
    enqueueSnackbar("I love snacks.", {
      autoHideDuration: 7000,
      content: (key) => (
        <CustomToast
          title={title}
          message={message}
          variant={variant}
          snackbarKey={key}
          closeSnackbar={closeSnackbar}
          type={type}
        />
      ),
    });
  }

  const handleUploadImage = (e: any) => {
    let filesTmp = Array.from(e.target.files); // Convert FileList to an array

    if (filesTmp.length + files.length > 3) {
      showCustomToast({title: "メッセージ", message: i18n.t('MSG_CHAT_MAX_ATTACHMENT_EXCEEDED'), variant: "error"})
      e.target.value = null;
      return;
    }
    for (const fileInput of filesTmp) {
      if (fileInput instanceof File) {
        // Check file size
        const sizeInMB = fileInput.size / (1024 * 1024);
        if (sizeInMB > MAX_FILE_SIZE.MESSAGE) {
          showCustomToast({title: "メッセージ", message: i18n.t('MSG_CHAT_MAX_ATTACHMENT_SIZE_EXCEEDED'), variant: "error"});
          filesTmp = filesTmp.filter(file => file.name !== fileInput.name);
        }
      }
    }
    if (filesTmp.length > 0) {
      const files_merge = [...files, ...filesTmp];
      setFiles(files_merge);
    }
    e.target.value = null;
  };

  const handleChangeTime = (name: string, value: string | number) => {
    let stringValue = String(value);
  
    const numericValue = stringValue.replace(/\D/g, "");
    if (numericValue.length <= 2) {
      const num = parseInt(numericValue, 10);
  
      if (numericValue === "" || (!isNaN(num) && num >= 0 && num <= 99)) {
        setFormData((prev: any) => ({
          ...prev,
          [name]: numericValue,
        }));
      }
    }
  };

  const getDateOverTime = (date, hour, minute): any => {
    const dateOverTime = new Date(date);
    dateOverTime.setHours(hour);
    dateOverTime.setMinutes(minute);
    dateOverTime.setSeconds(0);
    dateOverTime.setMilliseconds(0);
    return dateOverTime;
  }

  const validateSchedulePost = (formData: any): { hasError: boolean; errorState: ErrorState } => {
    let hasError = false;
    let errorState: ErrorState = {};

    if (!formData.publicScheduleTime) {
      hasError = true;
      errorState.publicScheduleTime = i18n.t('MSG_GLOBAL_PUBLISH_SCHEDULE_DATE_REQUIRED');
    }
    if (formData.publicScheduleHours || formData.publicScheduleMinutes) {
      if(formData.publicScheduleHours < 0 || formData.publicScheduleHours > 23) {
        hasError = true;
        errorState.publicScheduleHours = i18n.t('MSG_GLOBAL_HOUR_MINUTE_RANGE');
      }
      if(formData.publicScheduleMinutes < 0 || formData.publicScheduleMinutes > 59) {
        hasError = true;
        errorState.publicScheduleMinutes = i18n.t('MSG_GLOBAL_HOUR_MINUTE_RANGE');
      }
    }
    if(!formData.publicScheduleHours) {
      hasError = true;
      errorState.publicScheduleHours = i18n.t('MSG_GLOBAL_HOUR_REQUIRED');
    }
    if (!formData.publicScheduleMinutes) {
      hasError = true;
      errorState.publicScheduleMinutes = i18n.t('MSG_GLOBAL_MINUTE_REQUIRED');
    }
    if (formData.publicScheduleTime && formData.publicScheduleHours && formData.publicScheduleMinutes) {
      const scheduled = getDateOverTime(formData.publicScheduleTime, formData.publicScheduleHours, formData.publicScheduleMinutes);
      const minPublicTime = new Date(new Date().getTime() + 5 * 60000)

      if (scheduled < minPublicTime) {
        hasError = true;
        errorState.publicScheduleTime = errorState.publicScheduleHours = errorState.publicScheduleMinutes = publicTimeErrorMsg;
      }
    }

    return { hasError, errorState };
  };

  const scheduleErrorMsg = getTimeErrorMessage(errors.publicScheduleTime, errors.publicScheduleHours, errors.publicScheduleMinutes)

  const validateFormData = () => {
    formErrors = {}

    if (chatType == MESSAGE_TYPE.SCOUT && Object.keys(jobSelected).length === 0) {
      formErrors.jobID = i18n.t('MSG_CHAT_JOB_INFOMATION_REQUIRED');
    }

    if (formData.isPublicScheduled) {
      const { hasError, errorState } = validateSchedulePost(formData);

      if (hasError) {
        formErrors = { ...formErrors, ...errorState };
      }
    }

    const validateContentReq = validateFieldReq(formData.content, "本文");
    if(validateContentReq != true && files.length == 0) {
      formErrors.content = i18n.t('MSG_CHAT_ENTER_MESSAGES_CONTENT_REQUIRED');
    }

    setErrors(formErrors);
    return !Object.values(formErrors).some((error) => error);
  };

  const getScheduleDate = (date, hour, minute): any => {
    const scheduled = new Date(date);
    scheduled.setHours(hour);
    scheduled.setMinutes(minute);
    scheduled.setSeconds(0);
    scheduled.setMilliseconds(0);
    return scheduled;
  }

  const handleSubmitBulkMessages = () => {
    if(isSaving) {
      return
    }
    if (!validateFormData()) {
      return; 
    }
    dispatch(bulkMessagesActions.setIsSaving(true));
    dispatch(setIsOpenListJobPanel(false))
  
    const data = {
      files: files,
      talent_ids: recipients.map(item => item.talent_id).join(","),
      job_id: jobSelected?.id ? jobSelected?.id : '',
      chat_type: chatType,
      chat_content: formData?.content?.trim(),
      is_public_scheduled: formData?.isPublicScheduled,
      public_time: '',
    }
    let public_time = '';
    if (formData?.isPublicScheduled && formData.publicScheduleTime) {
      const localDate = getScheduleDate(formData.publicScheduleTime, formData.publicScheduleHours, formData.publicScheduleMinutes);
      public_time = localDate.toISOString();
    }
    data.public_time = public_time
    if(public_time) {
      data.public_time_jp_format = dayjs(public_time).format('YYYY-MM-DD HH:mm')
    } else {
      data.public_time_jp_format = dayjs(new Date()).format('YYYY-MM-DD HH:mm')
    }
    data.old_public_time_jp_format = oldPublicDate

    if(!bulkMessageDetail?.id) {
      dispatch(saveBuldMessage({formData: data}));
    } else {
      data.id = bulkMessageDetail?.id
      dispatch(updateBuldMessage({formData: data}));
    }
    
  };

  const insertTalentName = () => {
    const input = document.querySelector("#content");

    if (document.activeElement === input) {
      const start = input.selectionStart;
      const end = input.selectionEnd;
      const newValue = formData.content.slice(0, start) + "$[NAME]$" + formData.content.slice(end);
      setFormData(() => ({
        ...formData,
        ['content']: newValue,
      }));

      setTimeout(() => {
        input.selectionStart = input.selectionEnd = start + 3;
      }, 0);
    } else {
      setFormData(() => ({
        ...formData,
        ['content']: formData.content +  "$[NAME]$",
      }));
    }
  };

  const handleClose = () => {
    dispatch(bulkMessagesActions.setIsResetForm(false));
    dispatch(bulkMessagesActions.setRecipients([]));
    dispatch(bulkMessagesActions.setBulkMessageDetail({}));
    dispatch(bulkMessagesActions.setViewDetail(false));
    dispatch(setIsOpenListJobPanel(false))
    dispatch(setJobSelected({}));
    dispatch(setListJobRecommend([]));
    setFiles([])
    setFormData(initFormData);
    setErrors(initErrorData);

    onClose();
  };

  const handleGoToJob = (e: any, jobId: number) => {
    e.stopPropagation()
    handleClose();
    if(jobId) navigate("/company/job/detail/" + jobId);
  }

  const handleChangeIsPublicSchedu = (e: any) => {
    setFormData((prev: any) => ({ ...prev, isPublicScheduled: e.target.checked}))
    if(!e.target.checked) {
      setErrors((prev: any) => ({ ...prev, publicScheduleTime: null, publicScheduleHours: null, publicScheduleMinutes: null }))
    }
  }

  useEffect(() => {
    if (open && recipientName.length > 0) {
      const el = textRef.current;
      if (!el) return;

      const checkOverflow = () => {
        const clampedHeight = el.clientHeight;

        el.classList.remove("line-clamp-1");
        const fullHeight = el.scrollHeight;

        el.classList.add("line-clamp-1");

        setIsTextOverflow(fullHeight > clampedHeight);
      };

      checkOverflow();
    }
  }, [open, recipientName]);

  return (
    <Modal open={open} onClose={handleClose}>
      <div className="bg-white rounded-2xl p-6 min-w-[1040px] max-w-[1040px] h-[80vh] max-h-[800px] absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] outline-none overflow-y-auto custom-scrollbar flex flex-col">
        <div className={`flex items-center justify-between ${!viewDetail ? "mb-2" : "mb-4"}`}>
          {!viewDetail ? <h2 className="text-xl font-bold alu-text-base-2">
            {chatType == MESSAGE_TYPE.SCOUT ? 'スカウト' : 'メッセージ'}の一括送信
          </h2> : <h2 className="text-xl font-bold alu-text-base-2">
            一括送信した{chatType == MESSAGE_TYPE.SCOUT ? 'スカウト' : 'メッセージ'}
          </h2>}
          <CustomIconButton onClick={() => {
            onClose(); 
            dispatch(bulkMessagesActions.setViewDetail(false));
          }}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <g clipPath="url(#clip0_8574_265)">
                <path
                  d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
                  fill="currentColor"
                />
              </g>
              <defs>
                <clipPath id="clip0_8574_265">
                  <rect width="24" height="24" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </CustomIconButton>
        </div>
        {!viewDetail && <p className="text-xs alu-text-base-2 mb-4">
          選択したタレントに一括でスカウト、または、メッセージを送信することができます。 送信したメッセージは、一括送信リストで確認・修正することができます。
        </p>}
        <div className={`flex-1 flex flex-col p-2 ${viewDetail && "pb-4"}`}>
          {/* 宛先 */}
          <div className={`flex gap-4 mb-2`}>
            <label className="shrink-0 min-w-[3.5rem] block text-sm alu-text-base-2 font-bold mt-[11px]">宛先</label>
            <div className="w-full px-4 py-3 rounded border border-[#E0E0E0] flex gap-[0.625rem] justify-between">
              <p 
                ref={textRef} 
                className={`flex-1 ${isExpand ? "" : "line-clamp-1"} text-sm alu-text-base-2 leading-[21px]`}
                style={{ wordBreak: 'normal', overflowWrap: 'anywhere' }}
              >
                {recipientName.map((name, index) => (
                  <span key={index} className="inline-block">
                    {name}
                    {index < recipientName.length - 1 && "、"}
                  </span>
                ))}
              </p>
              
              {isTextOverflow && (
                <CustomIconButton 
                  size="sm" 
                  customStyle="!shrink-0 !w-[21px] !h-[21px]" 
                  onClick={(e) => {setIsExpand(prev => !prev); e.currentTarget.blur();}}
                >
                  <span className={`${isExpand ? "rotate-180" : ""} transition-transform`}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="8" viewBox="0 0 12 8" fill="none">
                      <path d="M10.59 0L6 4.58L1.41 0L0 1.41L6 7.41L12 1.41L10.59 0Z" fill="currentColor"/>
                    </svg>
                  </span>
                </CustomIconButton>
              )}
            </div>
          </div>

          {!viewDetail && <div className="flex items-center gap-4 mb-4">
            <label className="shrink-0 min-w-[3.5rem] block text-sm alu-text-base-2 font-bold"></label>
            <p className="text-xs alu-text-base-2">メッセージは選択した <strong>{recipientName.length}</strong> 件のタレントに個別に送付されます。</p>
          </div>}

          {/* 求人情報 */}
          { chatType == MESSAGE_TYPE.SCOUT &&
          <div className={`flex items-center gap-4 ${!viewDetail ? "mb-4" : "mb-2"}`}>
            <label className="shrink-0 min-w-[3.5rem] block text-sm alu-text-base-2 font-bold">求人情報</label>
            {!jobSelected?.id ? (
              <CustomButton
                disabled={isOpenListJobPanel || staffInfo?.type == STAFF_TYPE.GENERAL}
                onClick={() => dispatch(setIsOpenListJobPanel(true))}
                variant="outline"
                size="md"
              >
                求人を選ぶ
              </CustomButton>
              ) : (
                <>
                  <div onClick={() => !viewDetail && staffInfo?.type != STAFF_TYPE.GENERAL && dispatch(setIsOpenListJobPanel(true))}
                  className={`w-full flex items-center gap-4 justify-between rounded-[0.25rem] p-2 ${!viewDetail ? "border border-[#E0E0E0]" : ""} ${staffInfo?.type == STAFF_TYPE.ADMIN && !viewDetail && "hover:cursor-pointer" }`}>
                    <p className="flex-1 text-sm alu-text-base-1">
                      <u className={`${!viewDetail ? "alu-text-base-2" : "alu-text-base-1"} cursor-pointer hover:text-[#FF861A]`} 
                        onClick={(e: any) => handleGoToJob(e, jobSelected?.id)} disabled={staffInfo?.type == STAFF_TYPE.GENERAL}>
                          {jobSelected?.title}
                      </u>
                      <span className="ml-[0.875rem] alu-text-base-1">id: {formatPadStartNumber(jobSelected?.id, 5)}</span>
                    </p>
                    <div className="min-w-[100px] shrink-0 flex items-center gap-1 rounded-[0.25rem] bg-[#F7F7F7] px-2 py-[0.375rem]">
                      <span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="13"
                          height="13"
                          viewBox="0 0 13 13"
                          fill="none"
                        >
                          <circle cx="6.75" cy="6.5" r="6" fill={jobSelected?.is_available ? '#06C270' : '#BBB'} />
                        </svg>
                      </span>
                      <p className="text-sm alu-text-base-2">{jobSelected?.status_str}</p>
                    </div>
                  </div>
                </>
            )}
          </div>
          }

          {/* 送信予約 */}
          <div className={`flex items-center ${!viewDetail ? "gap-6 mb-6" : "gap-4 mb-4"}`}>
            <div className="shrink-0 flex items-center gap-3">
              <label className="shrink-0 block text-sm alu-text-base-2 font-bold">送信{!viewDetail ? "予約" : "日時"}</label>
              {!viewDetail && <CustomSwitch 
                checked={formData?.isPublicScheduled}
                onChange={(e: any) => handleChangeIsPublicSchedu(e)}
                disabled={staffInfo?.type == STAFF_TYPE.GENERAL}
              />}
            </div>
            {!viewDetail ? <div className="w-full">
              <div className="flex items-center gap-4">
                <div className="w-[264px]">
                  <CustomDatePicker
                    disabled={!formData?.isPublicScheduled || staffInfo?.type == STAFF_TYPE.GENERAL}
                    minDate={new Date()}
                    value={formData.publicScheduleTime}
                    onChange={(value: any) => setFormData((prev: any) => ({ ...prev, publicScheduleTime: value }))}
                    error={errors.publicScheduleTime}
                    placeholderText={""}
                    yearsInput={rangeYears}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-[4.5rem]">
                    <TimeInput
                      size="md"
                      type="text"
                      maxLength={2}
                      id="publicScheduleHours"
                      name="publicScheduleHours"
                      value={formData.publicScheduleHours}
                      onChange={handleChangeTime}
                      placeholder="00"
                      error={errors.publicScheduleHours}
                      errorText=""
                      disabled={!formData?.isPublicScheduled || staffInfo?.type == STAFF_TYPE.GENERAL}
                      customClassName="disabled:bg-[#F7F7F7] disabled:text-[#BBBBBB]"
                    />
                  </div>
                  <span>:</span>
                  <div className="w-[4.5rem]">
                    <TimeInput
                      size="md"
                      type="text"
                      maxLength={2}
                      id="publicScheduleMinutes"
                      name="publicScheduleMinutes"
                      value={formData.publicScheduleMinutes}
                      onChange={handleChangeTime}
                      placeholder="00"
                      error={errors.publicScheduleMinutes}
                      errorText=""
                      disabled={!formData?.isPublicScheduled || staffInfo?.type == STAFF_TYPE.GENERAL}
                      customClassName="disabled:bg-[#F7F7F7] disabled:text-[#BBBBBB]"
                    />
                  </div>
                </div>
              </div>
              {scheduleErrorMsg && (
                <p className="alu-text-error text-sm mt-2">
                  *{scheduleErrorMsg}
                </p>
              )}
            </div>
            : <p className="px-2 text-sm alu-text-base-2">{formatDateTime(bulkMessageDetail?.public_time)}</p>  
            }
          </div>

          {viewDetail && files.length > 0 && (
            <div className="pl-2 flex gap-4 items-end flex-wrap w-full mb-6">
              {files.map((file, idx) =>
                file?.type.startsWith("image") ? (
                  <div className="w-12 h-12 relative" key={idx}>
                    <img
                      className="w-full h-full object-cover rounded-lg"
                      src={file instanceof File ? URL.createObjectURL(file) : file.file_path}
                      alt={`img-${idx}`}
                    />
                  </div>
                ) : (
                  <p className="alu-text-base-2 text-sm">{file?.name}</p>
                )
              )}
            </div>
          )}

          {/* 本文 */}
          <div className="flex-1 mt-auto flex flex-col">
            <label className="block text-sm alu-text-base-2 font-bold mb-2">本文</label>
            <div className={`relative border border-[#E0E0E0] rounded-[0.25rem] ${!viewDetail && staffInfo?.type != STAFF_TYPE.GENERAL && "focus-within:border-[#FF861A]"} flex-1 flex flex-col`}>
              <CustomTextarea
                readOnly={viewDetail || staffInfo?.type == STAFF_TYPE.GENERAL}
                size="lg"
                id="content"
                name="content"
                isResizable={false}
                placeholder="入力してください"
                containerStyles={`flex-1 flex flex-col`}
                customStyles={`!rounded-b-none !px-4 !py-3 border-0 flex-1`}
                value={formData.content}
                onChange={(e: any) => setFormData((prev: any) => ({ ...prev, content: e.target.value }))}
              />

              {!viewDetail && files.length > 0 && (
                <div className="flex items-center gap-4 flex-wrap w-full pt-2 p-4">
                  {files.map((file, idx) =>
                    file?.type.startsWith("image") ? (
                      <div className="w-12 h-12 relative" key={idx}>
                        <button
                          className="absolute top-[-8px] right-[-8px] w-4 h-4 bg-[#FF861A] rounded-full flex items-center justify-center"
                          onClick={() =>
                            setFiles((prev) =>
                              prev?.filter((item, index) => idx !== index)
                            )
                          }
                          disabled={staffInfo?.type == STAFF_TYPE.GENERAL}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                          >
                            <g clipPath="url(#clip0_14189_11449)">
                              <path
                                d="M9.5 3.205L8.795 2.5L6 5.295L3.205 2.5L2.5 3.205L5.295 6L2.5 8.795L3.205 9.5L6 6.705L8.795 9.5L9.5 8.795L6.705 6L9.5 3.205Z"
                                fill="#343A3D"
                              />
                            </g>
                            <defs>
                              <clipPath id="clip0_14189_11449">
                                <rect width="12" height="12" fill="white"/>
                              </clipPath>
                            </defs>
                          </svg>
                        </button>
                        <img
                          className="w-full h-full object-cover rounded-lg"
                          src={file instanceof File ? URL.createObjectURL(file) : file.file_path}
                          alt={`img-${idx}`}
                        />
                      </div>
                    ) : (
                      <div
                        key={idx}
                        className="flex items-center py-1 px-2 border border-[#E0E0E0] rounded gap-4 text-sm"
                      >
                        <p className="truncate alu-text-base-2">{file?.name}</p>
                        <CustomIconButton
                          size="sm"
                          onClick={() =>
                            setFiles((prev) =>
                              prev?.filter((item, index) => idx !== index)
                            )
                          }
                          disabled={staffInfo?.type == STAFF_TYPE.GENERAL}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none" >
                            <g clipPath="url(#clip0_14189_11449)">
                              <path d="M9.5 3.205L8.795 2.5L6 5.295L3.205 2.5L2.5 3.205L5.295 6L2.5 8.795L3.205 9.5L6 6.705L8.795 9.5L9.5 8.795L6.705 6L9.5 3.205Z" fill="currentColor"/>
                            </g>
                            <defs>
                              <clipPath id="clip0_14189_11449">
                                <rect width="12" height="12" fill="white"/>
                              </clipPath>
                            </defs>
                          </svg>
                        </CustomIconButton>
                      </div>
                    )
                  )}
                </div>
              )}

              {!viewDetail && <div className={`z-10 px-4 py-[0.625rem] border-t border-t-[#E0E0E0] ${files.length > 0 ? '' : ''}`}>
                <input
                  ref={inputRef}
                  type="file"
                  className="hidden"
                  name="image"
                  id="image"
                  multiple
                  accept=".jpg,jpeg,.png,.pdf"
                  onChange={handleUploadImage}
                  disabled={staffInfo?.type == STAFF_TYPE.GENERAL}
                />
                <div className="flex items-center justify-between">
                  <div className="flex items-center flex-1">
                    <CustomIconButton onClick={() => inputRef.current.click()} disabled={staffInfo?.type == STAFF_TYPE.GENERAL}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <mask id="mask0_14620_4767" style={{ maskType: "alpha" }} maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24"><rect width="24" height="24" fill="#D9D9D9" /></mask>
                        <g mask="url(#mask0_14620_4767)"><path d="M5 21C4.45 21 3.97917 20.8042 3.5875 20.4125C3.19583 20.0208 3 19.55 3 19V5C3 4.45 3.19583 3.97917 3.5875 3.5875C3.97917 3.19583 4.45 3 5 3H13V5H5V19H19V11H21V19C21 19.55 20.8042 20.0208 20.4125 20.4125C20.0208 20.8042 19.55 21 19 21H5ZM6 17H18L14.25 12L11.25 16L9 13L6 17ZM17 9V7H15V5H17V3H19V5H21V7H19V9H17Z" fill="currentColor" /></g>
                      </svg>
                    </CustomIconButton>  
                    <span className="w-[1px] h-6 bg-[#E0E0E0] mx-4"></span>
                    <CustomButton type="button" variant="text" onMouseDown={insertTalentName} customStyle="!shrink-0" disabled={staffInfo?.type == STAFF_TYPE.GENERAL}>
                      氏名を挿入
                    </CustomButton>
                    <CustomTooltip 
                      content={
                        <div className="text-sm alu-text-base-2">
                          メッセージ内にタレントのフルネーム<br/>を表示することができます
                        </div>}
                      customStyles="!min-w-[270px] !max-w-[270px] !py-4 !left-[-50px]"
                    >
                      <span className="hover:cursor-pointer">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                          <mask id="mask0_3015_8847" style={{maskType: "alpha"}} maskUnits="userSpaceOnUse" x="0" y="0" width="18" height="18">
                            <rect width="18" height="18" fill="#D9D9D9"/>
                          </mask>
                          <g mask="url(#mask0_3015_8847)">
                            <path d="M8.96299 13.5C9.22549 13.5 9.44736 13.4094 9.62861 13.2281C9.80986 13.0469 9.90049 12.825 9.90049 12.5625C9.90049 12.3 9.80986 12.0781 9.62861 11.8969C9.44736 11.7156 9.22549 11.625 8.96299 11.625C8.70049 11.625 8.47861 11.7156 8.29736 11.8969C8.11611 12.0781 8.02549 12.3 8.02549 12.5625C8.02549 12.825 8.11611 13.0469 8.29736 13.2281C8.47861 13.4094 8.70049 13.5 8.96299 13.5ZM8.28799 10.6125H9.67549C9.67549 10.2 9.72236 9.875 9.81611 9.6375C9.90986 9.4 10.1755 9.075 10.613 8.6625C10.938 8.3375 11.1942 8.02812 11.3817 7.73437C11.5692 7.44062 11.663 7.0875 11.663 6.675C11.663 5.975 11.4067 5.4375 10.8942 5.0625C10.3817 4.6875 9.77549 4.5 9.07549 4.5C8.36299 4.5 7.78486 4.6875 7.34111 5.0625C6.89736 5.4375 6.58799 5.8875 6.41299 6.4125L7.65049 6.9C7.71299 6.675 7.85361 6.43125 8.07236 6.16875C8.29111 5.90625 8.62549 5.775 9.07549 5.775C9.47549 5.775 9.77549 5.88437 9.97549 6.10312C10.1755 6.32187 10.2755 6.5625 10.2755 6.825C10.2755 7.075 10.2005 7.30937 10.0505 7.52812C9.90049 7.74687 9.71299 7.95 9.48799 8.1375C8.93799 8.625 8.60049 8.99375 8.47549 9.24375C8.35049 9.49375 8.28799 9.95 8.28799 10.6125ZM9.00049 16.5C7.96299 16.5 6.98799 16.3031 6.07549 15.9094C5.16299 15.5156 4.36924 14.9812 3.69424 14.3062C3.01924 13.6312 2.48486 12.8375 2.09111 11.925C1.69736 11.0125 1.50049 10.0375 1.50049 9C1.50049 7.9625 1.69736 6.9875 2.09111 6.075C2.48486 5.1625 3.01924 4.36875 3.69424 3.69375C4.36924 3.01875 5.16299 2.48437 6.07549 2.09062C6.98799 1.69687 7.96299 1.5 9.00049 1.5C10.038 1.5 11.013 1.69687 11.9255 2.09062C12.838 2.48437 13.6317 3.01875 14.3067 3.69375C14.9817 4.36875 15.5161 5.1625 15.9099 6.075C16.3036 6.9875 16.5005 7.9625 16.5005 9C16.5005 10.0375 16.3036 11.0125 15.9099 11.925C15.5161 12.8375 14.9817 13.6312 14.3067 14.3062C13.6317 14.9812 12.838 15.5156 11.9255 15.9094C11.013 16.3031 10.038 16.5 9.00049 16.5ZM9.00049 15C10.6755 15 12.0942 14.4187 13.2567 13.2562C14.4192 12.0937 15.0005 10.675 15.0005 9C15.0005 7.325 14.4192 5.90625 13.2567 4.74375C12.0942 3.58125 10.6755 3 9.00049 3C7.32549 3 5.90674 3.58125 4.74424 4.74375C3.58174 5.90625 3.00049 7.325 3.00049 9C3.00049 10.675 3.58174 12.0937 4.74424 13.2562C5.90674 14.4187 7.32549 15 9.00049 15Z" fill="#808080"/>
                          </g>
                        </svg>
                      </span>
                    </CustomTooltip>
                  </div>
                  <div className="shrink-0">
                    <CustomButton
                      size="md"
                      disabled={isDisabledBtnSend || staffInfo?.type == STAFF_TYPE.GENERAL}
                      onClick={handleSubmitBulkMessages}
                    >
                      {!formData?.isPublicScheduled && <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
                        <path d="M0.00809523 16L17 8L0.00809523 0L0 6.22222L12.1429 8L0 9.77778L0.00809523 16Z" fill="currentColor"/>
                      </svg>}
                      {!formData?.isPublicScheduled ? '今すぐ送信' : '送信予約'}
                      {isLoadingSaveSchedule && (
                        <span><LoadingSpinner/></span>
                      )}
                    </CustomButton>
                  </div>
                </div>
              </div>}
            </div>
          </div> 
        </div>
        {isOpenListJobPanel && (<div className="fixed right-2 top-2 bottom-2 z-20"><JobPostPanel talentIds={formData?.talents} /></div>)}
      </div>
    </Modal>
  );
};

export default BulkScoutingDialog;
